import { apiGet } from '../api/readerApi';

const LIST_STORAGE_PREFIX = 'jeshua_content_list_';
const CHANNELING_TYPE = 'channeling';
const TEACHING_TYPE = 'teaching';
export const THIRD_PARTY_TYPE = 'third_party';

const memory = new Map();
const inFlight = new Map();

export const toApiLanguage = (language) => String(language || 'es').split('-')[0];

export const localDate = (date = new Date()) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const cacheKey = (...parts) => parts.join('|');

const readStorage = (key) => {
  try {
    const raw = window.sessionStorage.getItem(LIST_STORAGE_PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    window.sessionStorage.setItem(LIST_STORAGE_PREFIX + key, JSON.stringify(value));
  } catch (error) { /* storage unavailable or full */ }
};

export const clearContentCache = () => {
  memory.clear();
  inFlight.clear();
  try {
    Object.keys(window.sessionStorage)
      .filter((key) => key.startsWith(LIST_STORAGE_PREFIX))
      .forEach((key) => window.sessionStorage.removeItem(key));
  } catch (error) { /* storage unavailable */ }
};

export const normalizeItem = (item) => ({
  id: item.contentId,
  slug: item.slug,
  titulo: item.title,
  entidad: item.entity,
  canalizador: item.channeler,
  ensenanzas: item.teachings || [],
  minutos: item.minutes,
  imagen: item.image || '',
  minPlan: item.minPlan,
  publishedAt: item.publishedAt,
  idioma: item.language,
  esDelDia: Boolean(item.isDaily),
  bloqueada: Boolean(item.locked),
  palabras: item.words,
  firma: item.signature || '',
  extracto: item.excerpt || '',
  numero: item.number,
  codigo: item.code,
  tipoContenido: item.contentType,
  lote: item.releaseLot
});

export const normalizePiece = (piece) => ({
  ...normalizeItem(piece),
  parrafos: piece.paragraphs || [],
  blocks: piece.blocks || [],
  firma: piece.signature || '',
  musica: piece.music || null,
  audio: piece.audio || null,
  requiredPlan: piece.requiredPlan || null
});

export const fetchSearch = ({ site, lang, plan, token, q }) => {
  const key = cacheKey('search', site, toApiLanguage(lang), plan, q);
  return dedupe(key, async () => {
    const result = await apiGet(`/content/search?${query({ site, lang, q })}`, { token });
    if (!result.ok || !result.data) throw new Error(`search_${result.status}`);
    return (result.data.items || []).map((item) => ({ ...normalizeItem(item), onTopic: Boolean(item.onTopic) }));
  });
};

export const fetchDoors = ({ site, lang, plan, token }) => {
  const key = cacheKey('doors', site, toApiLanguage(lang), plan);
  return dedupe(key, async () => {
    const result = await apiGet(`/content/doors?${query({ site, lang })}`, { token });
    if (!result.ok || !Array.isArray(result.data)) throw new Error(`doors_${result.status}`);
    return result.data;
  });
};

const dedupe = (key, load) => {
  if (memory.has(key)) return Promise.resolve(memory.get(key));
  if (!inFlight.has(key)) {
    const pending = load()
      .then((value) => { memory.set(key, value); return value; })
      .finally(() => inFlight.delete(key));
    inFlight.set(key, pending);
  }
  return inFlight.get(key);
};

const query = ({ site, lang, ...extra }) => {
  const params = new URLSearchParams({ site, lang: toApiLanguage(lang) });
  Object.entries(extra).forEach(([name, value]) => { if (value) params.set(name, value); });
  return params.toString();
};

export const fetchCatalog = ({ site, lang, plan, token, type = CHANNELING_TYPE }) => {
  const key = cacheKey('list', type, site, toApiLanguage(lang), plan);
  return dedupe(key, async () => {
    const stored = readStorage(key);
    if (stored) return stored;
    const result = await apiGet(`/content?${query({ site, lang, type })}`, { token });
    if (!result.ok || !result.data) throw new Error(`catalog_${result.status}`);
    const items = (result.data.items || []).map(normalizeItem);
    writeStorage(key, items);
    return items;
  });
};

export const fetchPiece = ({ site, lang, plan, token, slug, date }) => {
  const key = cacheKey('piece', site, toApiLanguage(lang), plan, slug, date);
  return dedupe(key, async () => {
    const result = await apiGet(`/content/${encodeURIComponent(slug)}?${query({ site, lang, date })}`, { token });
    if (result.status === 404) return null;
    if (!result.ok || !result.data) throw new Error(`piece_${result.status}`);
    return normalizePiece(result.data);
  });
};

export const fetchThirdParty = ({ site, lang, plan, token, page, pageSize }) => {
  const key = cacheKey('third-party', site, toApiLanguage(lang), plan, page, pageSize);
  return dedupe(key, async () => {
    const result = await apiGet(
      `/content?${query({ site, lang, type: THIRD_PARTY_TYPE, page: String(page), pageSize: String(pageSize) })}`,
      { token }
    );
    if (!result.ok || !result.data) throw new Error(`third_party_${result.status}`);
    return { total: result.data.count, items: (result.data.items || []).map(normalizeItem) };
  });
};

export const fetchTeachings = (context) => fetchCatalog({ ...context, type: TEACHING_TYPE });

export const fetchTeaching = ({ number, ...context }) => fetchPiece({ ...context, slug: `ensenanza-${number}` });

export const fetchPreview = async ({ lang, token }) => {
  const result = await apiGet(`/content/preview/${encodeURIComponent(token)}?lang=${toApiLanguage(lang)}`);
  if (result.status === 404) return null;
  if (!result.ok || !result.data) throw new Error(`preview_${result.status}`);
  return { ...normalizePiece(result.data), bloqueada: false };
};

export const fetchDaily = ({ site, lang, plan, token, date }) => {
  const key = cacheKey('daily', site, toApiLanguage(lang), plan, date);
  return dedupe(key, async () => {
    const result = await apiGet(`/content/daily?${query({ site, lang, date })}`, { token });
    if (!result.ok || !result.data) throw new Error(`daily_${result.status}`);
    return normalizePiece(result.data);
  });
};
